import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useToast } from '../../components/common';
import Inspector, { Band, Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { Checklist } from '../../components/nucleus/Checklist';
import { ErrorEmpty } from '../../components/nucleus/Empty';
import { SHELVES, shelfOf } from '../../components/nucleus/shelves';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import { resolveReturnTo, leavingFor, leaveTo } from '../../utils/returnTo';
import { ago, until, dateOf, plural } from '../../nucleus/format';
import { PluginFace, RunDot } from './cells';
import ToolSheet from './ToolSheet';
import RequestSheet from './RequestSheet';
import SetupSheet, { STEP_IDS, LooseSchedules } from './SetupSheet';
import { ToolRows } from './PluginTree';
import { usePluginsGate } from './gate';
import AccessSheet, { NOT_VERIFIED, accessRowName } from './AccessSheet';
import { AsksFor, ChangeRows, ApprovedModal } from './ApprovalParts';
import { ConnectionSheet, CredentialsSheet, SettingsSheet, credentialFields, settingGroups } from './PluginSetup';
import { pluginsApi, autosApi, setupDoor } from './data/api';
import { firstSentence, toolsOf, primaryOf, approvalOf, flowItem, pluginItem } from './data/items';
import {
    useLoad, useSetupConfig, useRunFeed, usePluginRequests, usePluginAccess, useInstallPreview,
} from './data/hooks';
import {
    pluginStateWord, verifiedWord, uninstallWords, automationWord, triggerKindWord,
    outcomeWord, dotTone, turnOffAsk,
} from './data/words';
import {
    useSchemaNames, asksOf, setupSteps, firstUnmet, primaryWord, progressWord, reenterOf, needKeys,
} from './data/inspector';
import './plugin-inspector.css';

// ONE PLUGIN, wherever it is open (My plugins, the store, an app's link to
// /plugins/<id>/setup): what it is, what it will do, what it needs from you,
// what it did, who else may use it, and how to stop it. Three states and Off:
//   Not set up (the store): At a glance · Tools · It asks for · About;
//     the bar: Set up (it installs quietly and opens the setup), Website.
//   Setting up: Setup (Sign in · Keys · Settings · Turn on) · What changed
//     (when it changed) · Tools · …; the bar: Continue setup, Remove.
//   Ready / Off: Tools (each with how it runs, and Run) · It asks for · Tool
//     access · Recent runs · About; the bar: Run, the On/Off switch, Remove.
//   Withdrawn by its developer: a notice, and only Remove.
// In a group, changing a plugin is an admin's (greyed for a member, the
// reason said); running one is a member's whose realm lets them.

// What the address says came back: an approval, or the provider's answer to a
// connection (the OAuth callback lands on /plugins/<id>/setup, whose door
// brings the flags here). Read once, then taken off the address. The doors
// to a sheet (&tool= &tab=, &setup=, &access=) are watched, so a link that
// arrives while it is open opens its sheet too, and each is taken off once followed.
const FLAGS = ['approved', 'connected', 'not_connected', 'reason'];
export function flagNote(params, name) {
    if (params.get('approved')) return { text: `${name} is on.` };
    if (params.get('connected')) return { text: 'Signed in.' };
    if (params.has('not_connected')) {
        const why = params.get('reason');
        return {
            tone: 'live',
            text: why === 'access_denied' ? 'You declined at the service, so it isn’t signed in.' : `Not signed in${why ? `: ${why.replace(/_/g, ' ')}` : ''}.`,
        };
    }
    return null;
}

// A member sees an admin's acts on plugins greyed, each with the one same why (gate.js).
export { usePluginsGate };

const SHELF_WORD = Object.fromEntries(SHELVES.map((s) => [s.id, s.label]));

// ── the bands that read on their own ─────────────────────────────────────────

/** Read again when `tick` moves on (something changed), not on the first draw. */
function useAgain(res, tick) {
    const first = useRef(true);
    useEffect(() => { if (first.current) { first.current = false; return; } res.reload(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
}

function WaitingBand({ groupId, pluginId, tick, onOpen }) {
    const res = usePluginRequests(groupId, { pluginId });
    useAgain(res, tick);
    if (res.error && !res.data) return <Band title="Waiting for you"><ErrorEmpty what="what waits for you" error={res.error} retry={res.reload} /></Band>;
    const list = (res.data || []).filter((r) => r.status === 'pending');
    if (!list.length) return null;
    return (
        <Band title="Waiting for you" aside={list.length}>
            {list.map((r) => (
                <Row key={r.id} name={r.toolName || 'A run'} sub={[r.askedBy, r.why].filter(Boolean).join(' · ')}
                    end={r.expires ? `expires ${until(r.expires)}` : undefined} endTone="warn" onPress={() => onOpen(r)} />
            ))}
        </Band>
    );
}

// TOOL ACCESS: one row per connected app or agent ("Notebook · runs in
// Everyday"), its tools summed up at its end; pressing one opens its sheet.
// A plugin ofSelf hasn't verified: no app or agent can use its tools.
function AccessBand({ groupId, pluginId, verified, tick, onOpen }) {
    const res = usePluginAccess(groupId, pluginId);
    useAgain(res, tick);
    if (res.error && !res.data) return <Band title="Tool access"><ErrorEmpty what="its tool access" error={res.error} retry={res.reload} /></Band>;
    if (!res.data) return null; // reading, or an older server
    const ok = res.data.verified ?? verified;
    const grants = res.data.grants;
    return (
        <Band title="Tool access">
            {!ok && <Note tone="warn">{NOT_VERIFIED}</Note>}
            {ok && !grants.length && <Note>No app or agent is connected.</Note>}
            {ok && grants.map((g) => (
                <Row key={g.epId} name={accessRowName(g)} sub={g.summary} onPress={() => onOpen(g)} />
            ))}
        </Band>
    );
}

function AutomationsBand({ groupId, setup, tick, to, navigate }) {
    const flows = useLoad(() => autosApi.flows(groupId), [groupId, tick], { optional: true, fallback: { flows: [] }, keep: true });
    const all = setup?.automations || [];
    const mine = new Set(all.map((a) => String(a.id)));
    const autos = all.filter((a) => a.trigger_type === 'event' || a.trigger_type === 'manual');
    const inFlows = (flows.data?.flows || []).filter((f) => f?.id).map(flowItem).filter((f) => f.steps.some((s) => mine.has(String(s))));
    const stateOf = (a) => a.state || (!a.enabled ? 'off' : a.last_run_status === 'failed' || a.last_run_status === 'error' ? 'failing' : 'on');
    // the tool rows say how each tool runs; this band is only for what else runs it
    if (!autos.length && !inFlows.length) return null;
    return (
        <Band title="Automations" aside={<button type="button" className="nx-link" onClick={() => navigate(to('plugins', 'automations'))}>All automations</button>}>
            {autos.map((a) => {
                const st = stateOf(a);
                return (
                    <Row key={a.id} name={a.name} sub={triggerKindWord(a.trigger_type)} end={automationWord(st)} endTone={st === 'failing' ? 'live' : undefined}
                        onPress={() => navigate(to('plugins', 'automations', `open=${a.id}`))} />
                );
            })}
            {inFlows.map((f) => (
                <Row key={`flow-${f.id}`} name={f.name} sub={`Flow · ${plural(f.steps.length, 'step')}`}
                    onPress={() => navigate(to('plugins', 'automations', `open=${f.id}`))} />
            ))}
        </Band>
    );
}

// why a run did not go, under its outcome's word (which says Refused already)
function whyOf(r) {
    if (r.outcome !== 'refused' && r.error) return r.error;
    const said = r.refused ? r.refused.replace(/^Refused:\s*/, '') : null;
    return said ? `${said[0].toUpperCase()}${said.slice(1)}` : null;
}

function RecentRuns({ feed, tools, onAll, onOpen }) {
    const many = tools.length > 1;
    const toolName = (id) => tools.find((t) => String(t.id) === String(id ?? ''))?.name;
    return (
        <Band title="Recent runs" aside={<button type="button" className="nx-link" onClick={onAll}>All its runs</button>}>
            {feed.error && !feed.data && <ErrorEmpty what="its runs" error={feed.error} retry={feed.reload} />}
            {feed.data && !feed.data.runs.length && <Note>It hasn’t run yet.</Note>}
            {(feed.data?.runs || []).slice(0, 5).map((r) => (
                <Row
                    key={r.id}
                    name={<><RunDot tone={dotTone(r.outcome)} />{outcomeWord(r.outcome)}</>}
                    sub={[many ? r.toolName || toolName(r.toolId) : null, r.startedBy, whyOf(r)].filter(Boolean).join(' · ') || undefined}
                    end={ago(r.started, { never: 'never' })}
                    endTone={dotTone(r.outcome) === 'failed' ? 'live' : undefined}
                    onPress={() => onOpen(r)}
                />
            ))}
        </Band>
    );
}

export function UninstallBody({ preview }) {
    const w = uninstallWords(preview);
    if (!preview || (!w.goes.length && !w.stays.length)) {
        return <p>Its sign-in, keys, settings, how its tools run automatically, and every app’s access to its tools go. Its runs and what it made stay.</p>;
    }
    return (
        <div className="plugins-uninstall">
            {w.goes.length > 0 && (<><span className="plugins-sub">Goes</span><ul>{w.goes.map((x) => <li key={x}>{x}</li>)}</ul></>)}
            {w.stays.length > 0 && (<><span className="plugins-sub">Stays</span><ul>{w.stays.map((x) => <li key={x}>{x}</li>)}</ul></>)}
        </div>
    );
}

// ── the inspector ────────────────────────────────────────────────────────────

export default function PluginInspector({ item, returnTo = null, onClose, onChanged, onSetUp }) {
    const toast = useToast();
    const { groupId, inSpace, to, navigate } = usePlace();
    const access = useSpaceAccess();
    const gate = usePluginsGate();
    const [params, setParams] = useSearchParams();
    const [said] = useState(() => flagNote(params, item.name));
    const wanted = params.get('setup');
    const wantedAccess = params.get('access');
    const wantedTool = params.get('tool');
    // the sheet open over it: { kind: 'tool', toolId, tab } | { kind: 'setup', start } | 'connection' |
    // 'credentials' | { kind: 'settings', group } | { kind: 'access', grant } | { kind: 'request', request } | 'approved'
    const [sheet, setSheet] = useState(null);
    // Set up pressed: once it is installed and what its setup needs is read, the setup opens
    const [settingUp, setSettingUp] = useState(false);
    const [tick, setTick] = useState(0);
    const id = item.id;
    const declaredKey = (item.plugin?.tools || []).filter((t) => t && t.tool_id).map((t) => String(t.tool_id)).join('\n');
    const declared = useMemo(() => (declaredKey ? declaredKey.split('\n') : []), [declaredKey]);

    useEffect(() => {
        if (!FLAGS.some((k) => params.has(k))) return;
        setParams((p) => { const n = new URLSearchParams(p); FLAGS.forEach((k) => n.delete(k)); return n; }, { replace: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── what it is here ──
    const setupRes = useSetupConfig(groupId, id);
    const setup = setupRes.data;
    const lc = setup?.lifecycle || item.lifecycle || null;
    const installed = !!lc?.installed;
    const withdrawn = !!lc?.withdrawn;
    const perTool = useLoad(
        () => Promise.all(declared.map((t) => pluginsApi.setupConfig(id, groupId, t).catch(() => null))),
        [id, groupId, declaredKey], { enabled: installed && declared.length > 1, keep: true },
    );
    // a plugin that needs no account and a server that could not answer look
    // the same here: no Account step rather than a false "not connected"
    const oauthRes = useLoad(() => pluginsApi.oauth(id, groupId).catch(() => null), [id, groupId], { enabled: installed, keep: true });
    const oauth = !installed ? null : oauthRes.loading && !oauthRes.data ? undefined : oauthRes.data;
    const feed = useRunFeed(groupId, { pluginId: id, limit: 5 });
    const preview = useInstallPreview(groupId, id, { enabled: installed });
    const nameOf = useSchemaNames(groupId, item.plugin);

    const changed = (message) => {
        if (message) toast.success(message);
        setupRes.reload();
        if (installed && declared.length > 1) perTool.reload();
        if (installed) { oauthRes.reload(); preview.reload(); }
        feed.reload();
        setTick((t) => t + 1);
        onChanged?.();
    };

    // the item as its freshest lifecycle says it stands (Setting up, Ready, Off)
    const live = lc && item.plugin ? { ...pluginItem(item.plugin, lc), logo: item.logo } : { ...item, lifecycle: lc };
    const tools = toolsOf(live, setup?.tools);
    const asks = asksOf(live, setup);
    const configs = declared.length > 1
        ? declared.map((t, i) => ({ toolId: t, name: tools.find((x) => x.id === t)?.name || t, cfg: perTool.data?.[i] }))
        : [{ toolId: declared[0] || null, name: item.name, cfg: setup }];
    const credentials = credentialFields(configs);
    const groups = settingGroups(configs);
    const needCreds = needKeys(lc, 'credentials');
    const reenter = [...new Set([...reenterOf(setup), ...credentials.filter((f) => f.is_set && needCreds.includes(f.key)).map((f) => f.key)])];
    const approval = approvalOf(lc) || approvalOf(setup);
    const steps = setupSteps({ lifecycle: lc, oauth, credentials, groups, approval });
    const needsApproval = steps.find((s) => s.id === 'approve')?.status === 'missing';
    const changedSince = !!approval?.changed && needsApproval;
    const approved = installed && !needsApproval && !!approval?.approvedAt;
    const publicKey = setup?.enc_public_key || item.plugin?.enc_public_key || null;
    const state = lc?.state || 'not_installed';

    const openSetup = (start) => setSheet({ kind: 'setup', start: start || firstUnmet(steps) || 'approve' });
    const openTool = (toolId, tab = 'run') => setSheet({ kind: 'tool', toolId, tab });

    // the doors /plugins/<id>/setup (&setup=first) and /consent (&setup=approve):
    // the sheet at that step, once what it needs is read; &access=<ep_id> its access sheet
    const accessRes = usePluginAccess(groupId, id, { enabled: !!wantedAccess && installed && gate.isAdmin });
    const ready = !!setup && (!installed || oauth !== undefined) && (declared.length < 2 || !installed || !!perTool.data);
    // the router hands an updater the address as it was drawn, so each one-time key is taken off with the flags
    const followed = (keys) => setParams((p) => { const n = new URLSearchParams(p); [...keys, ...FLAGS].forEach((k) => n.delete(k)); return n; }, { replace: true });
    useEffect(() => {
        if (!settingUp || !ready || !installed) return;
        setSettingUp(false);
        const start = firstUnmet(steps);
        if (start) setSheet({ kind: 'setup', start });
    }, [settingUp, ready, installed]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (wantedTool != null) {
            setSheet({ kind: 'tool', toolId: wantedTool, tab: ['schedule', 'automatically'].includes(params.get('tab')) ? 'schedule' : 'run' });
            followed(['tool', 'tab']);
            return;
        }
        if (!ready) return;
        if (wantedAccess) {
            if (!accessRes.data && installed && gate.isAdmin && !accessRes.error) return;
            const g = (accessRes.data?.grants || []).find((x) => String(x.epId) === String(wantedAccess));
            if (g) setSheet({ kind: 'access', grant: g });
            followed(['access']);
            return;
        }
        if (!wanted) return;
        followed(['setup']);
        if (!installed || withdrawn) return;
        const start = wanted === 'approve' ? 'approve' : wanted === 'first' ? firstUnmet(steps) : STEP_IDS.includes(wanted) ? wanted : null;
        if (start) setSheet({ kind: 'setup', start });
    }, [wantedTool, wanted, wantedAccess, ready, accessRes.data, accessRes.error]); // eslint-disable-line react-hooks/exhaustive-deps

    const act = async (fn, message) => { await fn(); changed(message); };
    // SET UP: installed quietly, then its setup opens at the first step it needs
    const install = async () => {
        await pluginsApi.install(id, groupId);
        setSettingUp(true);
        changed();
        onSetUp?.();
    };
    // to the provider's own page; it comes back through the setup door, the
    // app's way back still on it
    const connect = async () => {
        const r = await pluginsApi.connect(id, groupId, setupDoor(id, groupId, returnTo));
        if (r?.authorize_url) leaveTo(r.authorize_url);
    };

    // ── the bar ──
    const primary = primaryOf(lc);
    const runHint = !access.runPlugins ? access.pluginHint : undefined;
    const runTool = tools.find((t) => t.runnable) || tools[0];
    const on = state !== 'paused';
    const actions = [];
    if (!withdrawn) {
        if (primary === 'install') actions.push({ id: 'setup', label: primaryWord(primary), primary: true, ...gate.off, run: install });
        if (['connect', 'credentials', 'settings', 'approve'].includes(primary)) {
            actions.push({ id: 'setup', label: primaryWord(primary), primary: true, ...gate.off, run: () => openSetup(firstUnmet(steps)) });
        }
        if (primary === 'run') {
            actions.push({ id: 'run', label: 'Run', primary: true, disabled: !!runHint, hint: runHint, run: () => openTool(runTool?.id ?? '', 'run') });
        }
        // ON/OFF, once it can run: Off asks first
        if (installed && (state === 'ready' || state === 'paused')) {
            actions.push({
                id: 'power', label: 'On', toggle: on, ...gate.off,
                confirm: on ? turnOffAsk(item.name) : undefined,
                run: () => (on
                    ? act(() => pluginsApi.pause(id, groupId), `${item.name} is off.`)
                    : act(() => pluginsApi.resume(id, groupId), `${item.name} is on.`)),
            });
        }
    }
    // BACK TO THE APP that sent someone here, validated: in-app it is followed,
    // another origin only once the person has seen where it goes
    const back = useMemo(() => resolveReturnTo(returnTo), [returnTo]);
    const host = leavingFor(back);
    if (back && !withdrawn) {
        actions.push({
            id: 'back', label: host ? `Back to ${host}` : 'Back to the app', icon: <ArrowLeft size={14} strokeWidth={1.8} aria-hidden="true" />,
            confirm: host ? { title: `Leave ofSelf for ${host}?`, label: `Continue to ${host}`, danger: false } : undefined,
            run: () => (host ? leaveTo(back.external) : navigate(back.internal)),
        });
    }
    if (!installed && item.website) {
        actions.push({
            id: 'website', label: 'Website', icon: <ExternalLink size={14} strokeWidth={1.8} aria-hidden="true" />,
            run: () => window.open(item.website, '_blank', 'noopener'),
        });
    }
    if (installed) {
        actions.push({
            id: 'remove', label: 'Remove', danger: true, ...gate.off,
            confirm: { title: `Remove ${item.name}?`, body: <UninstallBody preview={preview.data} /> },
            run: async () => { await pluginsApi.uninstall(id, groupId); changed(`${item.name} is removed${inSpace}.`); },
        });
    }
    const greyed = !gate.isAdmin && actions.some((a) => a.disabled && a.hint === gate.hint);

    // ── what it says ──
    const sub = changedSince ? 'Changed since you turned it on' : firstSentence(item.blurb);
    const verified = !!(setup?.verified ?? item.plugin?.is_verified);
    const held = (lc?.tools || []).filter((t) => t.schedule?.state === 'paused' && t.schedule?.reason === 'not_approved').length;
    const doneOpen = {
        account: () => (oauth ? setSheet({ kind: 'connection' }) : openSetup('account')),
        credentials: () => setSheet({ kind: 'credentials' }),
        settings: () => (groups.length === 1 ? setSheet({ kind: 'settings', group: 0 }) : openSetup('settings')),
        approve: () => openSetup('approve'),
    };
    // Setup's checklist: only the steps it needs
    const checks = steps.filter((s) => s.status !== 'not_needed').map((s) => ({
        id: s.id, label: s.title, status: s.status, sub: s.sub,
        onOpen: s.status === 'done' ? doneOpen[s.id] : () => openSetup(s.id),
    }));
    const hosts = asks.data.here ? 'ofSelf' : asks.data.hosts.length ? asks.data.hosts.join(', ') : 'A place it does not name';
    const runsHref = to('plugins', 'runs', `plugin=${id}`);
    const loose = lc?.unassigned_schedules || [];
    const readError = setupRes.error && !setup;

    return (
        <Inspector
            face={<PluginFace item={item} />}
            tag={`Plugin · ${pluginStateWord(state)}`}
            title={item.name}
            sub={sub}
            actions={actions}
            onClose={onClose}
        >
            {greyed && <p className="plugins-held">{`${gate.hint}.`}</p>}
            {said && <Note tone={said.tone}>{said.text}</Note>}
            {withdrawn && <Note tone="live">{`Its developer withdrew ${item.name}. It no longer runs, and nothing can be set up for it. Remove it to clear what it left here.`}</Note>}
            {readError && <ErrorEmpty what={`${item.name}’s setup`} error={setupRes.error} retry={setupRes.reload} />}

            {!installed && (
                <Band title="At a glance">
                    <Facts items={[
                        { label: 'Verified', value: verifiedWord(verified), tone: verified ? undefined : 'warn' },
                        { label: 'Apps and agents', value: verified ? 'Can use it' : 'Can’t use it until it’s verified', tone: verified ? undefined : 'warn' },
                        item.madeBy && { label: 'Made by', value: item.madeBy },
                        shelfOf(item.plugin) && { label: 'Category', value: SHELF_WORD[shelfOf(item.plugin)] || shelfOf(item.plugin) },
                        { label: 'Price', value: item.plugin?.pricing_display?.headline || 'Free' },
                        item.listed && { label: 'Listed', value: dateOf(item.listed) },
                        item.updated && { label: 'Updated', value: dateOf(item.updated) },
                    ]} />
                </Band>
            )}

            {installed && !withdrawn && live.settingUp && setup && oauth !== undefined && (
                <Band title="Setup" aside={progressWord(steps)}>
                    <Checklist label="Setup" items={checks} />
                </Band>
            )}

            {installed && !withdrawn && changedSince && (
                <Band title="What changed">
                    <Note>{`Turned on ${approval.approvedAt ? `${dateOf(approval.approvedAt)}, ` : ''}for an earlier version.`}</Note>
                    <Note tone="warn">{`Held until you turn it on again: ${[held ? `${plural(held, 'tool')} that ${held === 1 ? 'runs' : 'run'} automatically` : null, 'apps and agents'].filter(Boolean).join(' · ')}.`}</Note>
                    <ChangeRows approval={approval} tools={tools} />
                </Band>
            )}

            {installed && !withdrawn && (
                <WaitingBand groupId={groupId} pluginId={id} tick={tick} onOpen={(r) => setSheet({ kind: 'request', request: r })} />
            )}

            <Band title="Tools" aside={tools.length > 1 ? tools.length : undefined}>
                {installed && !withdrawn
                    ? <ToolRows item={live} tools={tools} runHint={runHint} onOpenTool={(_, t, tab) => openTool(t, tab)} />
                    : tools.map((t) => {
                        const a = asks.tools.find((x) => String(x.id) === String(t.id));
                        return (
                            <Row key={t.id || 'tool'} name={t.name} sub={firstSentence(t.description) || undefined}
                                end={a?.irreversible ? 'Can’t be undone' : undefined} endTone={a?.irreversible ? 'warn' : undefined} />
                        );
                    })}
            </Band>

            {installed && !withdrawn && loose.length > 0 && (
                <Band title="Runs automatically, with no tool">
                    <LooseSchedules loose={loose} tools={tools} gate={gate} pluginId={id} groupId={groupId} onChanged={changed} />
                </Band>
            )}

            <Band title="It asks for" aside={approved ? <button type="button" className="nx-link" onClick={() => setSheet({ kind: 'approved' })}>What you agreed to</button> : undefined}>
                {setup || !setupRes.loading ? <AsksFor asks={asks} nameOf={nameOf} /> : null}
            </Band>

            {installed && !withdrawn && gate.isAdmin && <AccessBand groupId={groupId} pluginId={id} verified={verified} tick={tick} onOpen={(g) => setSheet({ kind: 'access', grant: g })} />}

            {installed && !withdrawn && <AutomationsBand groupId={groupId} setup={setup} tick={tick} to={to} navigate={navigate} />}

            {installed && (
                <RecentRuns feed={feed} tools={tools} onAll={() => navigate(runsHref)}
                    onOpen={(r) => navigate(to('plugins', 'runs', `plugin=${id}&open=${r.id}`))} />
            )}

            <Band title="About">
                <Note>{item.blurb || 'Its developer has not said yet.'}</Note>
                <Facts items={[
                    installed && item.madeBy && { label: 'Made by', value: item.madeBy },
                    installed && { label: 'Verified', value: verifiedWord(verified) },
                    installed && item.listed && { label: 'Listed', value: dateOf(item.listed) },
                    installed && item.updated && { label: 'Updated', value: dateOf(item.updated) },
                    { label: 'Data goes to', value: hosts },
                ]} />
                {(item.website || item.privacyPolicy) && (
                    <p className="plugins-links">
                        {item.website && <a className="nx-link" href={item.website} target="_blank" rel="noopener noreferrer">Website</a>}
                        {item.privacyPolicy && <a className="nx-link" href={item.privacyPolicy} target="_blank" rel="noopener noreferrer">Privacy policy</a>}
                    </p>
                )}
            </Band>

            {sheet?.kind === 'tool' && (
                <ToolSheet
                    item={live} tools={tools} toolId={sheet.toolId} tab={sheet.tab || 'run'}
                    onTab={(t) => setSheet((cur) => (cur?.kind === 'tool' ? { ...cur, tab: t } : cur))} onSetup={(step) => openSetup(step)}
                    groupId={groupId} access={access} gate={gate} onClose={() => setSheet(null)} onChanged={() => changed()} onInstall={install}
                />
            )}
            {sheet?.kind === 'setup' && setup && (
                <SetupSheet
                    name={item.name} pluginId={id} groupId={groupId} setup={setup} oauth={oauth || null} credentials={credentials} groups={groups}
                    reenter={reenter} publicKey={publicKey} asks={asks} nameOf={nameOf} approval={approval} steps={steps} tools={tools}
                    start={sheet.start} gate={gate} onConnect={connect} onChanged={changed}
                    onApprove={async () => { await pluginsApi.approve(id, groupId); changed(); }}
                    onReady={() => { setSheet(null); toast.success(`${item.name} is ready.`); }}
                    onClose={() => setSheet(null)}
                />
            )}
            {sheet?.kind === 'connection' && oauth && (
                <ConnectionSheet pluginId={id} groupId={groupId} name={item.name} oauth={oauth} gate={gate} onConnect={connect}
                    onClose={() => setSheet(null)} onChanged={changed} />
            )}
            {sheet?.kind === 'credentials' && (
                <CredentialsSheet pluginId={id} groupId={groupId} fields={credentials} reenter={reenter} publicKey={publicKey} gate={gate}
                    onClose={() => setSheet(null)} onApprove={() => openSetup('approve')}
                    onSaved={(m, o) => { if (!o?.keep) setSheet(null); changed(m); }} />
            )}
            {sheet?.kind === 'settings' && groups[sheet.group] && (
                <SettingsSheet pluginId={id} groupId={groupId} group={groups[sheet.group]} gate={gate}
                    title={groups.length > 1 ? `Settings · ${groups[sheet.group].names.join(', ')}` : 'Settings'}
                    onClose={() => setSheet(null)} onApprove={() => openSetup('approve')}
                    onSaved={(m, o) => { if (!o?.keep) setSheet(null); changed(m); }} />
            )}
            {sheet?.kind === 'access' && (
                <AccessSheet pluginId={id} pluginName={item.name} groupId={groupId} grant={sheet.grant} gate={gate} verified={verified}
                    onClose={() => setSheet(null)} onSaved={() => setTick((t) => t + 1)} />
            )}
            {sheet?.kind === 'approved' && (
                <ApprovedModal name={item.name} approval={approval} asks={asks} nameOf={nameOf} onClose={() => setSheet(null)} />
            )}
            {sheet?.kind === 'request' && (
                <RequestSheet groupId={groupId} requestId={sheet.request.id} onClose={() => setSheet(null)}
                    onAnswered={() => { setSheet(null); changed(); }} />
            )}
        </Inspector>
    );
}
